import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@/components/ui/Spinner";
import {
  fetchRemoteAppVersion,
  getLocalBuildId,
  isNewerRemoteBuild,
} from "@/lib/appVersion";
import { buildDisplayWebSocketUrl } from "@/lib/publicApiClient";
import type { PlanungBoard } from "@/lib/montageplanung";
import {
  getDisplayBoard,
  getDisplayDeviceStatus,
  getOrCreateDisplayDeviceId,
  registerDisplayDevice,
  type DisplayDeviceStatus,
  type DisplayThemeMode,
} from "@/lib/montageplanDisplay";
import { addDays, toIso } from "@/pages/montageplanung/dateUtils";
import {
  MP_DISPLAY_SIDE_DAYS,
  MP_DISPLAY_TODAY_OFFSET,
} from "@/pages/montageplanung/layoutConstants";
import {
  computeDisplayDateRange,
  MontageplanDisplayBoard,
} from "@/pages/montageplan/VollbildBoard";
import { useDisplayDeviceTheme } from "@/pages/montageplan/useDisplayDeviceTheme";
import { DISPLAY_UI_SCALE_DEFAULT, normalizeDisplayUiScale } from "@/pages/montageplan/displayUiScale";
import "./montageplanDisplay.css";

type PagePhase = "registering" | "pending" | "approved" | "rejected" | "revoked" | "error";

const DISPLAY_VERSION_CHECK_MS = 2 * 60 * 1000;

function formatDeviceIdShort(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

function reloadDisplayPage(): void {
  window.location.reload();
}

export function MontageplanVollbildPage() {
  const deviceId = useMemo(() => getOrCreateDisplayDeviceId(), []);
  const [phase, setPhase] = useState<PagePhase>("registering");
  const [themeMode, setThemeMode] = useState<DisplayThemeMode>("dark");
  const [uiScale, setUiScale] = useState(DISPLAY_UI_SCALE_DEFAULT);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [board, setBoard] = useState<PlanungBoard | null>(null);
  const [today, setToday] = useState(() => new Date());
  const [rangeFrom, setRangeFrom] = useState(() =>
    addDays(new Date(), -(MP_DISPLAY_TODAY_OFFSET + MP_DISPLAY_SIDE_DAYS)),
  );
  const [rangeTo, setRangeTo] = useState(() =>
    addDays(new Date(), MP_DISPLAY_SIDE_DAYS + 14),
  );
  const wsRef = useRef<WebSocket | null>(null);
  const phaseRef = useRef(phase);
  const localBuildIdRef = useRef(getLocalBuildId());
  phaseRef.current = phase;

  const loadBoard = useCallback(async () => {
    const data = await getDisplayBoard({
      device_id: deviceId,
      date_from: toIso(rangeFrom),
      date_to: toIso(rangeTo),
    });
    setBoard(data);
  }, [deviceId, rangeFrom, rangeTo]);

  const refreshBoardSilent = useCallback(async () => {
    try {
      const data = await getDisplayBoard({
        device_id: deviceId,
        date_from: toIso(rangeFrom),
        date_to: toIso(rangeTo),
      });
      setBoard(data);
    } catch {
      // Verbindung kurz weg – Anzeige behält letzten Stand.
    }
  }, [deviceId, rangeFrom, rangeTo]);

  const applyStatus = useCallback(
    (
      status: DisplayDeviceStatus,
      options?: { themeMode?: DisplayThemeMode | null; uiScale?: number | null },
    ) => {
      if (options?.themeMode) setThemeMode(options.themeMode);
      if (options?.uiScale != null) setUiScale(normalizeDisplayUiScale(options.uiScale));
      if (status === "approved") {
        setPhase("approved");
        setStatusMessage(null);
        return;
      }
      if (status === "pending") {
        setPhase("pending");
        setStatusMessage(null);
        return;
      }
      if (status === "rejected") {
        setPhase("rejected");
        return;
      }
      if (status === "revoked") {
        setPhase("revoked");
      }
    },
    [],
  );

  useDisplayDeviceTheme(themeMode);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const reg = await registerDisplayDevice({
          device_id: deviceId,
          user_agent: navigator.userAgent,
        });
        if (cancelled) return;
        applyStatus(reg.status, {
          themeMode: reg.theme_mode ?? "dark",
          uiScale: reg.ui_scale ?? DISPLAY_UI_SCALE_DEFAULT,
        });
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        setStatusMessage(err instanceof Error ? err.message : "Registrierung fehlgeschlagen.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deviceId, applyStatus]);

  useEffect(() => {
    if (phase === "registering" || phase === "error") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const st = await getDisplayDeviceStatus(deviceId);
        if (cancelled) return;
        applyStatus(st.status, {
          themeMode: st.theme_mode ?? "dark",
          uiScale: st.ui_scale ?? DISPLAY_UI_SCALE_DEFAULT,
        });
      } catch {
        // offline
      }
    };
    void poll();
    const intervalMs =
      phase === "pending" ? 4000 : phase === "approved" ? 60000 : 8000;
    const timer = window.setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase, deviceId, applyStatus]);

  useEffect(() => {
    if (phase !== "approved") return;
    void loadBoard().catch((err) => {
      setPhase("error");
      setStatusMessage(err instanceof Error ? err.message : "Planung konnte nicht geladen werden.");
    });
  }, [phase, loadBoard]);

  useEffect(() => {
    if (phase === "registering" || phase === "error") return;

    let closed = false;
    let reconnectTimer: number | null = null;

    const connect = () => {
      if (closed) return;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      const ws = new WebSocket(buildDisplayWebSocketUrl(deviceId));
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(String(event.data)) as { event?: string };
          const name = payload.event ?? "";
          if (name === "app_update" || name === "app_updated" || name === "app_deployed") {
            reloadDisplayPage();
            return;
          }
          if (name === "display_device_approved") {
            setPhase("approved");
            setStatusMessage(null);
          }
          if (phaseRef.current === "approved" && (
            name === "montageplanung_changed" ||
            name.startsWith("montageplanung_") ||
            name.startsWith("employee-times_")
          )) {
            void refreshBoardSilent();
          }
          if (name === "display_device_theme_changed") {
            const data = (payload as { data?: { theme_mode?: DisplayThemeMode } }).data;
            if (data?.theme_mode) setThemeMode(data.theme_mode);
          }
          if (name === "display_device_ui_scale_changed") {
            const data = (payload as { data?: { ui_scale?: number } }).data;
            if (data?.ui_scale != null) setUiScale(normalizeDisplayUiScale(data.ui_scale));
          }
          if (name === "display_device_rejected") setPhase("rejected");
          if (name === "display_device_revoked") setPhase("revoked");
        } catch {
          // ignore
        }
      };
      ws.onclose = () => {
        if (closed) return;
        reconnectTimer = window.setTimeout(connect, 5000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [phase, deviceId, refreshBoardSilent]);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    if (phase === "registering" || phase === "error") return;

    let cancelled = false;
    const checkVersion = async () => {
      try {
        const remote = await fetchRemoteAppVersion();
        if (cancelled || !remote) return;
        if (isNewerRemoteBuild(localBuildIdRef.current, remote.buildId)) {
          reloadDisplayPage();
        }
      } catch {
        // offline
      }
    };

    void checkVersion();
    const timer = window.setInterval(() => void checkVersion(), DISPLAY_VERSION_CHECK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "approved") return;

    let midnightTimer: number | null = null;

    const tick = () => {
      const now = new Date();
      setToday((prev) => {
        if (
          prev.getFullYear() === now.getFullYear() &&
          prev.getMonth() === now.getMonth() &&
          prev.getDate() === now.getDate()
        ) {
          return prev;
        }
        const span = computeDisplayDateRange(now, 14);
        setRangeFrom(span.from);
        setRangeTo(span.to);
        return now;
      });
    };

    const scheduleMidnight = () => {
      const now = new Date();
      const next = new Date(now);
      next.setHours(24, 0, 5, 0);
      const delay = Math.max(1000, next.getTime() - now.getTime());
      midnightTimer = window.setTimeout(() => {
        tick();
        void refreshBoardSilent();
        scheduleMidnight();
      }, delay);
    };

    scheduleMidnight();
    const interval = window.setInterval(tick, 60_000);
    return () => {
      if (midnightTimer != null) window.clearTimeout(midnightTimer);
      window.clearInterval(interval);
    };
  }, [phase, refreshBoardSilent]);

  if (phase === "registering") {
    return (
      <div className="mp-display-pending">
        <Spinner className="h-8 w-8" />
        <p className="text-sm text-label-secondary">Gerät wird registriert …</p>
      </div>
    );
  }

  if (phase === "pending") {
    return (
      <div className="mp-display-pending">
        <h1 className="text-xl font-semibold">Freigabe ausstehend</h1>
        <p className="max-w-md text-sm text-label-secondary">
          Dieses Gerät wurde registriert und wartet auf Freigabe durch einen Administrator im
          Admin-Bereich unter „Vollbild-Anzeigen“.
        </p>
        <p className="mp-display-pending__code">Gerät: {formatDeviceIdShort(deviceId)}</p>
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (phase === "rejected" || phase === "revoked") {
    return (
      <div className="mp-display-pending">
        <h1 className="text-xl font-semibold">
          {phase === "rejected" ? "Zugriff abgelehnt" : "Freigabe widerrufen"}
        </h1>
        <p className="max-w-md text-sm text-label-secondary">
          {phase === "revoked"
            ? "Die Freigabe wurde widerrufen. Ein Administrator kann das Gerät erneut freigeben."
            : "Dieses Gerät darf die Montageplanung nicht anzeigen. Bitte wenden Sie sich an einen Administrator."}
        </p>
        <p className="mp-display-pending__code">Gerät: {formatDeviceIdShort(deviceId)}</p>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="mp-display-pending">
        <h1 className="text-xl font-semibold">Fehler</h1>
        <p className="max-w-md text-sm text-label-secondary">
          {statusMessage ?? "Die Anzeige konnte nicht geladen werden."}
        </p>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="mp-display-pending">
        <Spinner className="h-8 w-8" />
        <p className="text-sm text-label-secondary">Montageplanung wird geladen …</p>
      </div>
    );
  }

  return (
    <MontageplanDisplayBoard
      board={board}
      rangeFrom={rangeFrom}
      rangeTo={rangeTo}
      today={today}
      uiScale={uiScale}
    />
  );
}
